import { useParams, Link } from 'react-router-dom'
import React from "react";
import { formatISO, parseISO } from 'date-fns';

import { LeaguePageNav } from "../components/league/LeaguePageNav";
import { CalendarSlider } from "../components/league/Slider";
import { MatchesBlock } from "../components/league/Matches";
import { Popup } from "../components/common/Popup";
import {
    useCreateLeagueInviteLink,
    useGenerateFixtures,
    useGetLeagueBySlug
} from '../api/leaguesApi.js';
import { useStartMultiplayerSeason } from '../api/multiplayerApi.js';
import { useAuth } from "../context/AuthContext";
import { toast } from 'react-toastify';
import {
    canBeginSeason,
    isAutoSquadSeasonLaunch,
    isPreSeason
} from "../utils/seasonStatus";
import { getSeasonStartedMessage } from "../utils/seasonKickoff";
import { getOwnerJoinabilityErrorMessage } from "../utils/inviteErrors";
import { QuickplayMatchHistoryPage } from "./QuickplayMatchHistoryPage";


function LeagueFixturesPage() {
    const { slug } = useParams();
    const { data: slugLeagueData, refetch: refetchLeagueData } = useGetLeagueBySlug(slug);

    // NEW: normalise API response to handle { league: {...} } or legacy { ... }
    const rawLeague = slugLeagueData?.data;
    const league = rawLeague?.league ?? rawLeague;
    const leagueTeamCount = Array.isArray(league?.teams) ? league.teams.length : 0;
    const generateFixturesMutation = useGenerateFixtures();
    const startMultiplayerSeason = useStartMultiplayerSeason();
    const createInviteLink = useCreateLeagueInviteLink();
    const { user } = useAuth();

    // Determine if current user is the league owner
    const leagueOwnerId = league?.owner?._id || league?.owner;
    const isLeagueOwner = leagueOwnerId && user?._id && leagueOwnerId.toString() === user._id.toString();

    // Add state for the selected date, initialized to today
    const [selectedDate, setSelectedDate] = React.useState(new Date());

    // Handler to update the selected date
    const handleDateChange = (newDate) => {
        // Ensure newDate is a Date object before setting
        if (newDate instanceof Date && !isNaN(newDate)) {
            setSelectedDate(newDate);
            console.log("Selected date updated:", newDate);
        } else {
            console.error("Invalid date provided to handleDateChange:", newDate);
            // Optionally try parsing if it's a string
            try {
                const parsedDate = parseISO(newDate); // Or other parsing logic
                if (!isNaN(parsedDate)) {
                    setSelectedDate(parsedDate);
                    console.log("Selected date updated after parsing:", parsedDate);
                } else {
                     console.error("Could not parse date string:", newDate);
                }
            } catch (e) {
                console.error("Error parsing date string:", newDate, e);
            }
        }
    };

    const generateFixtures = (leagueId) => {
        generateFixturesMutation.mutate(leagueId, {
            onSuccess: (data) => {
                console.log("Fixtures generated successfully:", data);
                toast.success(data.message || "Fixtures generated successfully!");
                // Refresh league data or navigate as needed (we stay on fixtures page)
                refetchLeagueData();
            },
            onError: (error) => {
                console.error("Error generating fixtures:", error);
                const errorMessage = error.response?.data?.error || "Failed to generate fixtures. Please try again.";
                toast.error(errorMessage);
            },
        });
    };

    const [showConfirmPopup, setShowConfirmPopup] = React.useState(false);

    const handleBeginSeason = async () => {
        if (!league?._id) {
            console.error("League ID not available");
            toast.error("Could not identify the league. Please refresh.");
            return;
        }

        // The multiplayer start endpoint schedules the round-1 kickoff; odd team
        // counts are handled by bye rounds in the fixture scheduler.
        if (isAutoSquadSeasonLaunch(league)) {
            try {
                const result = await startMultiplayerSeason.mutateAsync({
                    leagueId: league._id,
                    teamCount: leagueTeamCount
                });
                toast.success(getSeasonStartedMessage(result?.roundOneKickoff));
                refetchLeagueData();
            } catch (error) {
                console.error("Error starting multiplayer season:", error);
                toast.error(error.response?.data?.error || "Failed to start the season. Please try again.");
            }
            return;
        }

        // If fixtures already exist, prompt for confirmation to create next season.
        if (league?.matches && league.matches.length > 0) {
            setShowConfirmPopup(true);
            return;
        }

        generateFixtures(league._id);
    };

    const confirmGenerateNextSeason = () => {
        setShowConfirmPopup(false);
        if (league?._id) {
            generateFixtures(league._id);
        }
    };

    const handleCopyInviteLink = async () => {
        if (!league?._id) {
            toast.error("Could not identify the league. Please refresh.");
            return;
        }
        try {
            const { url } = await createInviteLink.mutateAsync(league._id);
            await navigator.clipboard.writeText(url);
            toast.success("Invite link copied! Share it with your friends.");
        } catch (error) {
            console.error("Error copying invite link:", error);
            toast.error(getOwnerJoinabilityErrorMessage(error) || error.response?.data?.error || "Could not copy the invite link. Please try again.");
        }
    };


    if (league?.gameMode === 'quickplay') {
        return <QuickplayMatchHistoryPage league={league} />;
    }

    return (
        <div className="page-league">
            <div className="wrapper">
                <LeaguePageNav slug={slug} />

                <div className="title-block">
                    {isLeagueOwner && canBeginSeason(league) && (
                        <div className="ctrl-block owner-season-controls">
                            {isPreSeason(league) && (
                                <button
                                    type="button"
                                    className="submit-btn"
                                    onClick={handleCopyInviteLink}
                                    disabled={createInviteLink.isPending}
                                >
                                    {createInviteLink.isPending ? 'Copying...' : 'Copy invite link'}
                                </button>
                            )}
                            <button
                                type="button"
                                className="submit-btn"
                                onClick={handleBeginSeason}
                                disabled={generateFixturesMutation.isLoading || startMultiplayerSeason.isPending || leagueTeamCount < 2}
                            >
                                {(generateFixturesMutation.isLoading || startMultiplayerSeason.isPending) ? 'Starting...' : 'Begin season'}
                            </button>
                            {leagueTeamCount < 2 && (
                                <div className="empty-message">
                                    At least one more team needs to join the league before the season can start.
                                </div>
                            )}
                        </div>
                    )}
                    <h1 >{league?.name}</h1>

                    <div className="ctrl-block">
                        <Link to={"/switch-league"} className="read-more-btn">
                            Switch league

                            <span className="ico icon-arrow-right" />
                        </Link>
                    </div>
                </div>

                {/* Pass selectedDate and handler to CalendarSlider */}
                <CalendarSlider
                    selectedDate={selectedDate}
                    onDateChange={handleDateChange}
                />

                {/* Pass leagueId and selectedDate to MatchesBlock */}
                {league?._id ? (
                     <>
                        <h4 className="match-day">
                            League Fixtures for {selectedDate.toLocaleDateString()}
                        </h4>
                        <MatchesBlock
                            leagueId={league._id}
                            selectedDate={selectedDate} // Pass the selected date
                        />
                     </>
                ) : (
                    <div className="empty-message">
                        Loading league data or no matches scheduled...
                    </div>
                )}

                {/* Confirmation Popup */}
                <Popup popOpen={showConfirmPopup} close={() => setShowConfirmPopup(false)}>
                    <h3>Season {league?.season || 1} fixtures already created</h3>
                    <p>Proceed with creating Season {(league?.season || 1) + 1} fixtures?</p>
                    <div className="btn-block flex-block">
                        <button type="button" className="submit-btn cancel-btn" onClick={() => setShowConfirmPopup(false)}>Cancel</button>
                        <button type="button" className="submit-btn trans-btn confirm-btn" onClick={confirmGenerateNextSeason}>Confirm</button>
                    </div>
                </Popup>

            </div>


        </div>
    )
}

export {LeagueFixturesPage}
